import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Center, Divider, Spinner } from 'src/components/atoms';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { Modal, Stepper } from 'src/components/molecules';
import { CancelModal } from 'src/components/molecules/CancelModal';
import { CasesUploadFile } from 'src/components/organisms/CasesUploadFile';
import { useUploadCases } from 'src/hooks/cases/useUploadCases';
import { usePreventNavigation } from 'src/hooks/navigation';
import { trackCaseImportFlowExited } from 'src/utils/analytics/track/caseImport';
import * as Types from '../../../types';
import { FeatureFlag } from '../../../types';
import { CaseReview, PreImportChecklist } from './steps';
import { CategorySelection } from './steps/CategorySelection';
import { PageContainer, Top } from './UploadCases.styles';

export enum UploadScreen {
  PRE_IMPORT_CHECKLIST,
  CATEGORY_SELECTION,
  UPLOAD,
  CASE_REVIEW,
  IMPORT_PROGRESS,
}

const steps = [
  'Pre-Import Checklist',
  'Upload & Map Data',
  'Review',
  'Import Progress',
];

export const UploadCases = () => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const features = useSelector((state: Types.State) => state.features);
  const isRoutingEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.ROUTING,
  );
  const isDynamicQuestionsEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.DYNAMIC_QUESTIONS_V1,
  );

  useEffect(() => {
    if (isDynamicQuestionsEnabled && !steps.includes('Category Selection')) {
      steps.splice(1, 0, 'Category Selection');
    }
  }, [isDynamicQuestionsEnabled]);

  const {
    loading,
    screen,
    onImportSuccess,
    tableColumns,
    tableData,
    duplicateTableColumns,
    duplicateTableData,
    commit,
    hasActiveJob,
    activeJobPercentage,
    importedCaseIds,
    setUploadEvidences,
    uploadEvidences,
    stateProperties,
    importFileName,
    totalCasesInCSV,
    setScreen,
    template,
    setTemplate,
    dynamicCategory,
    setDynamicCategory,
    dynamicFormId,
    setDynamicFormId,
  } = useUploadCases();

  const {
    showCancellationModal,
    preventNavigation,
    setNextLocation,
    onStay,
    onLeave,
    setShowCancellationModal,
    ref,
  } = usePreventNavigation(
    screen === UploadScreen.CASE_REVIEW ||
      screen === UploadScreen.CATEGORY_SELECTION,
    '/uploadCases',
  );

  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [ref, screen]);

  const cancelModalText =
    'Are you sure you want to leave the case import process? Your progress will be lost.';

  if (loading)
    return (
      <Center>
        <Spinner variant="secondary" size="xlarge" />
      </Center>
    );

  const ImporterHeader = () => {
    return (
      <Top>
        <Stepper activeStep={screen} steps={steps} />
        <Divider variant="horizontal" />
      </Top>
    );
  };

  const handleExitImport = () => {
    if (segmentEnabled && stateProperties?.organisationId) {
      trackCaseImportFlowExited({
        organisation_id: Number(stateProperties.organisationId),
        user_id: String(stateProperties.userId),
        step_where_exited: 'review',
      });
    }
    onLeave();
  };

  switch (screen) {
    case UploadScreen.PRE_IMPORT_CHECKLIST:
      return (
        <>
          <PageContainer>
            <ImporterHeader />
            <PreImportChecklist
              setShowUploadModal={setShowUploadModal}
              setScreen={setScreen}
              hasActiveJob={hasActiveJob}
              activeJobPercentage={activeJobPercentage}
              stateProperties={stateProperties}
            />
          </PageContainer>
          {showUploadModal && (
            <CasesUploadFile
              onSuccess={onImportSuccess}
              steps={steps}
              isOpen={showUploadModal}
              setIsOpen={setShowUploadModal}
              templateKey={template}
              dynamicFormId={dynamicFormId}
            />
          )}
        </>
      );

    case UploadScreen.CATEGORY_SELECTION:
      return (
        <>
          <PageContainer>
            <ImporterHeader />
            <CategorySelection
              setShowUploadModal={setShowUploadModal}
              stateProperties={stateProperties}
              setTemplate={setTemplate}
              dynamicCategory={dynamicCategory}
              setDynamicCategory={setDynamicCategory}
              dynamicFormId={dynamicFormId}
              setDynamicFormId={setDynamicFormId}
            />
          </PageContainer>
          {showUploadModal && (
            <CasesUploadFile
              onSuccess={onImportSuccess}
              steps={steps}
              isOpen={showUploadModal}
              setIsOpen={setShowUploadModal}
              templateKey={template}
              dynamicFormId={dynamicFormId}
            />
          )}
        </>
      );

    case UploadScreen.UPLOAD:
      return (
        <>
          <CasesUploadFile
            onSuccess={onImportSuccess}
            steps={steps}
            isOpen={showUploadModal}
            setIsOpen={setShowUploadModal}
            templateKey={template}
            dynamicFormId={dynamicFormId}
          />
          <Modal
            open={showConfirmationModal}
            setOpen={setShowConfirmationModal}
            onConfirm={() => setShowConfirmationModal(false)}
            onCancel={() => setShowConfirmationModal(false)}
            size="medium"
            title="No changes needed"
            description="Your database of cases and reports is currently up-to-date."
          />
        </>
      );
    case UploadScreen.CASE_REVIEW:
      return (
        <PageContainer ref={ref}>
          <CancelModal
            size="medium"
            title="Leave the CSV upload?"
            description={cancelModalText}
            showCancellationModal={showCancellationModal}
            preventNavigation={preventNavigation}
            setNextLocation={setNextLocation}
            onConfirm={onStay}
            onCancel={handleExitImport}
            setOpen={setShowCancellationModal}
            setShowCancellationModal={setShowCancellationModal}
          />
          <ImporterHeader />
          <CaseReview
            tableColumns={tableColumns}
            tableData={tableData}
            duplicateTableColumns={duplicateTableColumns}
            duplicateTableData={duplicateTableData}
            commit={commit}
            isRoutingEnabled={isRoutingEnabled}
            importedCaseIds={importedCaseIds}
            setUploadEvidences={setUploadEvidences}
            uploadEvidences={uploadEvidences}
            importFileName={importFileName}
            totalCasesInCSV={totalCasesInCSV}
            stateProperties={stateProperties}
          />
        </PageContainer>
      );
    case UploadScreen.IMPORT_PROGRESS:
      return <Redirect to="/uploadCasesHistory" />;
  }

  return null;
};
